export type StudentStatus = 'On track' | 'Approaching' | 'Needs attention'

export type ProgressMetricKey =
  | 'knowledge'
  | 'understanding'
  | 'application'
  | 'engagement'
  | 'character'
  | 'attitude'

export type StudentProgressMetric = {
  key: ProgressMetricKey
  label: string
  score: number
  icon: string
  description: string
  meaning: string
  summary: string
  evidence: string
  measured?: boolean
}

export type MathematicsEvidenceKey = 'shows' | 'explains' | 'applies'

export type MathematicsDetail = {
  metrics: {
    label: string
    value: string
    icon: string
    iconColor: string
    background: string
    sublabel: string
    progress?: number
  }[]
  weeks: {
    week: string
    date: string
    title: string
    activity: string
    levels: string
    progress: number
    accuracy: string
    time: string
    status: 'Completed' | 'In progress' | 'Current week' | 'Planned'
    color: string
    icon: string
  }[]
  nextTeachingStep: { title: string; description: string }
  evidence: {
    weekLabel: string
    checks: Record<MathematicsEvidenceKey, boolean>
    items: { key: MathematicsEvidenceKey; label: string }[]
    observation: string
  }
}

export type TeacherStudent = {
  id: string
  name: string
  initials: string
  avatar: string
  status: StudentStatus
  progress: number
  progressMetrics: StudentProgressMetric[]
  streak: number
  timeSpent: string
  lastActive: string
  focus: string
  birthday: string
  subjects: {
    name: string
    progress: number
    color: string
    icon: string
    locked?: boolean
  }[]
  dimensions: { name: string; score: number; color: string; icon: string }[]
  notes: { date: string; text: string }[]
  mathematicsDetail: MathematicsDetail
}

type MetricStory = {
  score: number
  summary: string
  evidence: string
}

type StudentProfileInput = Omit<
  TeacherStudent,
  'status' | 'progress' | 'progressMetrics' | 'mathematicsDetail'
> & {
  metricStories: Record<ProgressMetricKey, MetricStory>
  mathematics: {
    minutes: number
    nextStep: { title: string; description: string }
    observation: string
  }
}

const metricDefinitions: Record<
  ProgressMetricKey,
  Pick<StudentProgressMetric, 'label' | 'icon' | 'description' | 'meaning'>
> = {
  knowledge: {
    label: 'Knowledge',
    icon: 'i-lucide-book-open',
    description:
      'Ability to retain, recall and recognize basic information and facts.',
    meaning: 'What students know.',
  },
  understanding: {
    label: 'Understanding',
    icon: 'i-lucide-lightbulb',
    description:
      'Being able to explain information in their own words, summarize, classify, predict outcomes and make connections.',
    meaning: 'What students grasp.',
  },
  application: {
    label: 'Application',
    icon: 'i-lucide-star',
    description:
      'Ability to use learned knowledge and concepts, solve problems, follow instructions and use tools.',
    meaning: 'How students use the information they know.',
  },
  engagement: {
    label: 'Engagement',
    icon: 'i-lucide-heart',
    description:
      'Active participation in class, curiosity, collaboration and perseverance across cognitive, behavioral, social and emotional engagement.',
    meaning: 'How students participate, learn, do and feel.',
  },
  character: {
    label: 'Character',
    icon: 'i-lucide-shield-check',
    description: 'Inner moral values and a sense of social responsibility.',
    meaning: 'Responsibility, respect, honesty and kindness.',
  },
  attitude: {
    label: 'Attitude',
    icon: 'i-lucide-smile',
    description:
      'Ability to regulate emotions, react positively to learning, view failure as an opportunity to learn and respond constructively to feedback.',
    meaning: 'How students view themselves and react to challenges.',
  },
}

const metricOrder = Object.keys(metricDefinitions) as ProgressMetricKey[]

function average(values: number[]) {
  return Math.round(
    values.reduce((sum, value) => sum + value, 0) / values.length,
  )
}

function getStatus(progress: number): StudentStatus {
  if (progress > 70) return 'On track'
  if (progress > 55) return 'Approaching'
  return 'Needs attention'
}

function buildMathematicsDetail(
  profile: StudentProfileInput,
  metricScores: Record<ProgressMetricKey, number>,
): MathematicsDetail {
  const mathematics = profile.subjects.find(
    (subject) => subject.name === 'Mathematics',
  )!
  const score = mathematics.progress
  const accuracy = average([metricScores.knowledge, metricScores.understanding])
  const completedLevels = Math.round((score / 100) * 30)
  const answeredQuestions = Math.round((accuracy / 100) * 80)
  const weeklyProgress = [
    Math.max(score - 12, 10),
    Math.max(score - 6, 10),
    Math.max(score - 2, 10),
    score,
  ]
  const weeklyAccuracy = weeklyProgress.map((progress, index) =>
    Math.max(30, Math.min(98, accuracy - 6 + index * 4)),
  )

  return {
    metrics: [
      {
        label: 'Games played',
        value: String(Math.max(2, Math.round(score / 18))),
        icon: 'i-lucide-gamepad-2',
        iconColor: '#42ad51',
        background: '#edfcf0',
        sublabel: '',
      },
      {
        label: 'Levels completed',
        value: `${completedLevels}/30`,
        icon: 'i-lucide-chart-no-axes-column-increasing',
        iconColor: '#1678e5',
        background: '#edf6ff',
        sublabel: `${score}%`,
        progress: score,
      },
      {
        label: 'Accuracy',
        value: `${accuracy}%`,
        icon: 'i-lucide-target',
        iconColor: '#ff9000',
        background: '#fff8e7',
        sublabel: `${answeredQuestions}/80 answers`,
      },
      {
        label: 'Time spent',
        value: `${profile.mathematics.minutes} min`,
        icon: 'i-lucide-clock-3',
        iconColor: '#1678e5',
        background: '#edf6ff',
        sublabel: '',
      },
    ],
    weeks: [
      {
        week: 'Week 1',
        date: 'Mon 6 Jan',
        title: 'Counting objects',
        activity: 'Count & Match',
        levels: `${Math.round((weeklyProgress[0]! / 100) * 8)}/8 levels`,
        progress: weeklyProgress[0]!,
        accuracy: `${weeklyAccuracy[0]}%`,
        time: `${Math.max(8, Math.round(profile.mathematics.minutes * 0.22))} min`,
        status: weeklyProgress[0] === 100 ? 'Completed' : 'In progress',
        color: weeklyProgress[0] === 100 ? '#42ad51' : '#1678e5',
        icon: weeklyProgress[0] === 100 ? 'i-lucide-check' : 'i-lucide-circle',
      },
      {
        week: 'Week 2',
        date: 'Mon 13 Jan',
        title: 'Recognising 2D shapes',
        activity: 'Shape Sort',
        levels: `${Math.round((weeklyProgress[1]! / 100) * 6)}/6 levels`,
        progress: weeklyProgress[1]!,
        accuracy: `${weeklyAccuracy[1]}%`,
        time: `${Math.max(7, Math.round(profile.mathematics.minutes * 0.18))} min`,
        status: weeklyProgress[1] === 100 ? 'Completed' : 'In progress',
        color: weeklyProgress[1] === 100 ? '#42ad51' : '#1678e5',
        icon: weeklyProgress[1] === 100 ? 'i-lucide-check' : 'i-lucide-circle',
      },
      {
        week: 'Week 3',
        date: 'Mon 20 Jan',
        title: 'Sorting objects',
        activity: 'Sort & Count',
        levels: `${Math.round((weeklyProgress[2]! / 100) * 6)}/6 levels`,
        progress: weeklyProgress[2]!,
        accuracy: `${weeklyAccuracy[2]}%`,
        time: `${Math.max(7, Math.round(profile.mathematics.minutes * 0.2))} min`,
        status: 'In progress',
        color: '#1678e5',
        icon: 'i-lucide-circle',
      },
      {
        week: 'Week 4',
        date: 'Mon 27 Jan',
        title: 'Number bonds to 10',
        activity: 'Make Ten',
        levels: `${Math.round((weeklyProgress[3]! / 100) * 10)}/10 levels`,
        progress: weeklyProgress[3]!,
        accuracy: `${weeklyAccuracy[3]}%`,
        time: `${Math.max(8, Math.round(profile.mathematics.minutes * 0.28))} min`,
        status: 'Current week',
        color: '#1678e5',
        icon: 'i-lucide-circle',
      },
      {
        week: 'Week 5',
        date: 'Mon 3 Feb',
        title: 'Comparing lengths',
        activity: 'Planned',
        levels: '0/0 levels',
        progress: 0,
        accuracy: '—',
        time: '—',
        status: 'Planned',
        color: '#94a3b8',
        icon: 'i-lucide-circle',
      },
    ],
    nextTeachingStep: profile.mathematics.nextStep,
    evidence: {
      weekLabel: 'Week 4 · Number bonds to 10',
      checks: {
        shows: metricScores.knowledge > 55,
        explains: metricScores.understanding > 70,
        applies: metricScores.application > 70,
      },
      items: [
        { key: 'shows', label: 'Shows with objects' },
        { key: 'explains', label: 'Explains a method' },
        { key: 'applies', label: 'Applies to a new example' },
      ],
      observation: profile.mathematics.observation,
    },
  }
}

function createStudentProfile(profile: StudentProfileInput): TeacherStudent {
  const activeSubjects = profile.subjects.filter((subject) => !subject.locked)
  const progress = average(activeSubjects.map((subject) => subject.progress))
  const characterScore = average(
    profile.dimensions.map((dimension) => dimension.score),
  )
  const metricScores = Object.fromEntries(
    metricOrder.map((key) => [
      key,
      key === 'character' ? characterScore : profile.metricStories[key].score,
    ]),
  ) as Record<ProgressMetricKey, number>
  const progressMetrics = metricOrder.map((key) => ({
    key,
    ...metricDefinitions[key],
    score: metricScores[key],
    summary: profile.metricStories[key].summary,
    evidence: profile.metricStories[key].evidence,
  }))

  return {
    ...profile,
    status: getStatus(progress),
    progress,
    progressMetrics,
    mathematicsDetail: buildMathematicsDetail(profile, metricScores),
  }
}

type AdditionalStudentSeed = {
  id: string
  name: string
  initials: string
  avatar: string
  birthday: string
  mathematics: number
  metrics: Record<Exclude<ProgressMetricKey, 'character'>, number>
  character: [number, number, number, number]
  streak: number
  timeSpent: string
  lastActive: string
  focus: string
  notes: [string, string]
}

function scoreBand(score: number) {
  if (score > 70) return 'securely'
  if (score > 55) return 'with growing consistency'
  return 'with guided support'
}

function createMetricStory(
  firstName: string,
  key: Exclude<ProgressMetricKey, 'character'>,
  score: number,
): MetricStory {
  const stories = {
    knowledge: {
      summary: `${firstName} recalls and recognizes recently taught number facts ${scoreBand(score)}.`,
      evidence: `${firstName}'s recent counting and number bond exercises show a knowledge score of ${score}%.`,
    },
    understanding: {
      summary: `${firstName} explains mathematical ideas and makes connections between examples ${scoreBand(score)}.`,
      evidence: `Classroom explanations and sorting activities support an understanding score of ${score}%.`,
    },
    application: {
      summary: `${firstName} uses learned strategies in new problems ${scoreBand(score)}.`,
      evidence: `Independent and guided problem solving tasks support an application score of ${score}%.`,
    },
    engagement: {
      summary: `${firstName} participates, collaborates and perseveres during learning activities ${scoreBand(score)}.`,
      evidence: `Attendance, activity completion and teacher observations support an engagement score of ${score}%.`,
    },
    attitude: {
      summary: `${firstName} responds to feedback and approaches mistakes as learning opportunities ${scoreBand(score)}.`,
      evidence: `Responses to correction and challenging activities support an attitude score of ${score}%.`,
    },
  }

  return { score, ...stories[key] }
}

function createAdditionalStudent(seed: AdditionalStudentSeed): TeacherStudent {
  const firstName = seed.name.split(' ')[0]!
  const [responsibility, respect, honesty, kindness] = seed.character
  const weakestLearningMetric = (
    ['knowledge', 'understanding', 'application'] as const
  ).reduce((weakest, key) =>
    seed.metrics[key] < seed.metrics[weakest] ? key : weakest,
  )
  const nextSteps = {
    knowledge: {
      title: 'Rehearse core number facts',
      description:
        'Use counters and a short retrieval game before asking the student to recall the same facts without prompts.',
    },
    understanding: {
      title: 'Explain the method aloud',
      description:
        'Model one example, then ask the student to explain each step using objects and their own words.',
    },
    application: {
      title: 'Apply one strategy independently',
      description:
        'Complete one worked example together, then ask the student to use the same strategy in a new problem.',
    },
  }

  return createStudentProfile({
    id: seed.id,
    name: seed.name,
    initials: seed.initials,
    avatar: seed.avatar,
    birthday: seed.birthday,
    streak: seed.streak,
    timeSpent: seed.timeSpent,
    lastActive: seed.lastActive,
    focus: seed.focus,
    subjects: [
      {
        name: 'Mathematics',
        progress: seed.mathematics,
        color: '#1678E5',
        icon: 'i-lucide-calculator',
      },
      {
        name: 'English',
        progress: 0,
        color: '#FF9000',
        icon: 'i-lucide-message-square-text',
        locked: true,
      },
      {
        name: 'Science',
        progress: 0,
        color: '#8A42F5',
        icon: 'i-lucide-flask-conical',
        locked: true,
      },
    ],
    dimensions: [
      {
        name: 'Responsibility',
        score: responsibility,
        color: getProgressStatusColor(responsibility),
        icon: 'i-lucide-user-round',
      },
      {
        name: 'Respect',
        score: respect,
        color: getProgressStatusColor(respect),
        icon: 'i-lucide-heart',
      },
      {
        name: 'Honesty',
        score: honesty,
        color: getProgressStatusColor(honesty),
        icon: 'i-lucide-shield-check',
      },
      {
        name: 'Kindness',
        score: kindness,
        color: getProgressStatusColor(kindness),
        icon: 'i-lucide-smile',
      },
    ],
    metricStories: {
      knowledge: createMetricStory(
        firstName,
        'knowledge',
        seed.metrics.knowledge,
      ),
      understanding: createMetricStory(
        firstName,
        'understanding',
        seed.metrics.understanding,
      ),
      application: createMetricStory(
        firstName,
        'application',
        seed.metrics.application,
      ),
      engagement: createMetricStory(
        firstName,
        'engagement',
        seed.metrics.engagement,
      ),
      character: {
        score: 0,
        summary: `${firstName}'s character score reflects responsibility, respect, honesty and kindness observed in class.`,
        evidence: `Character is the average of Responsibility ${responsibility}%, Respect ${respect}%, Honesty ${honesty}% and Kindness ${kindness}%.`,
      },
      attitude: createMetricStory(firstName, 'attitude', seed.metrics.attitude),
    },
    notes: [
      { date: '18 Jun 2026', text: seed.notes[0] },
      { date: '11 Jun 2026', text: seed.notes[1] },
    ],
    mathematics: {
      minutes: Math.max(24, Math.round(seed.mathematics * 0.78)),
      nextStep: nextSteps[weakestLearningMetric],
      observation: `${firstName} demonstrates knowledge at ${seed.metrics.knowledge}%, explains methods at ${seed.metrics.understanding}% and applies learning independently at ${seed.metrics.application}%.`,
    },
  })
}

function getProgressStatusColor(score: number) {
  if (score > 70) return '#48AE50'
  if (score > 55) return '#FF9000'
  return '#E00000'
}

const coreStudents: TeacherStudent[] = [
  createStudentProfile({
    id: 'princess-owusu',
    name: 'Princess Owusu',
    initials: 'PO',
    avatar: '#F5CDA8',
    streak: 12,
    timeSpent: '2h 45m',
    lastActive: 'Today',
    focus: 'Applying strong understanding to more challenging problems',
    birthday: '12 Jan 2019',
    subjects: [
      {
        name: 'Mathematics',
        progress: 78,
        color: '#1678E5',
        icon: 'i-lucide-calculator',
      },
      {
        name: 'English',
        progress: 0,
        color: '#FF9000',
        icon: 'i-lucide-message-square-text',
        locked: true,
      },
      {
        name: 'Science',
        progress: 0,
        color: '#8A42F5',
        icon: 'i-lucide-flask-conical',
        locked: true,
      },
    ],
    dimensions: [
      {
        name: 'Responsibility',
        score: 78,
        color: '#48AE50',
        icon: 'i-lucide-user-round',
      },
      { name: 'Respect', score: 84, color: '#48AE50', icon: 'i-lucide-heart' },
      {
        name: 'Honesty',
        score: 80,
        color: '#48AE50',
        icon: 'i-lucide-shield-check',
      },
      { name: 'Kindness', score: 86, color: '#48AE50', icon: 'i-lucide-smile' },
    ],
    metricStories: {
      knowledge: {
        score: 75,
        summary:
          'Princess recalls key facts confidently and recognizes familiar concepts across her active mathematics work.',
        evidence:
          'She accurately recalled number bonds and counting patterns in recent exercises.',
      },
      understanding: {
        score: 78,
        summary:
          'Princess explains familiar ideas clearly and makes useful connections between examples.',
        evidence:
          'She explained her counting strategy and compared two solution methods in her own words.',
      },
      application: {
        score: 72,
        summary:
          'Princess applies learning independently in familiar tasks and is beginning to transfer it to harder problems.',
        evidence:
          'She completed multi step activities successfully but occasionally needs a prompt on unfamiliar questions.',
      },
      engagement: {
        score: 82,
        summary:
          'Princess participates actively, asks thoughtful questions and persists when work becomes challenging.',
        evidence:
          'She completed every assigned activity this week and contributed during group discussions.',
      },
      character: {
        score: 0,
        summary:
          'Princess consistently demonstrates responsibility, respect, honesty and kindness in class.',
        evidence:
          'Character is the average of Responsibility 78%, Respect 84%, Honesty 80% and Kindness 86%.',
      },
      attitude: {
        score: 80,
        summary:
          'Princess responds positively to feedback and treats mistakes as opportunities to improve.',
        evidence:
          'She revised an incorrect mathematics answer without becoming discouraged and explained what she changed.',
      },
    },
    notes: [
      {
        date: '16 Jun 2026',
        text: 'Princess recalled number bonds accurately, explained her strategy clearly and completed the extension task with only one prompt. She stayed engaged and used feedback positively.',
      },
      {
        date: '3 Jun 2026',
        text: 'Princess showed kindness during group work and helped a classmate without taking over. Continue offering unfamiliar application problems to stretch her thinking.',
      },
    ],
    mathematics: {
      minutes: 68,
      nextStep: {
        title: 'Extend number bonds to word problems',
        description:
          'Ask the student to solve two unfamiliar number bond stories and explain why each method works.',
      },
      observation:
        'Princess shows number bonds with objects and explains her method. She is beginning to apply the strategy to unfamiliar word problems.',
    },
  }),
  createStudentProfile({
    id: 'kojo-boateng',
    name: 'Kojo Boateng',
    initials: 'KB',
    avatar: '#CDE9C7',
    streak: 7,
    timeSpent: '1h 45m',
    lastActive: 'Yesterday',
    focus: 'Explaining his thinking and applying learning independently',
    birthday: '8 Sep 2019',
    subjects: [
      {
        name: 'Mathematics',
        progress: 64,
        color: '#1678E5',
        icon: 'i-lucide-calculator',
      },
      {
        name: 'English',
        progress: 0,
        color: '#FF9000',
        icon: 'i-lucide-message-square-text',
        locked: true,
      },
      {
        name: 'Science',
        progress: 0,
        color: '#8A42F5',
        icon: 'i-lucide-flask-conical',
        locked: true,
      },
    ],
    dimensions: [
      {
        name: 'Responsibility',
        score: 68,
        color: '#FF9000',
        icon: 'i-lucide-user-round',
      },
      { name: 'Respect', score: 73, color: '#48AE50', icon: 'i-lucide-heart' },
      {
        name: 'Honesty',
        score: 66,
        color: '#FF9000',
        icon: 'i-lucide-shield-check',
      },
      { name: 'Kindness', score: 69, color: '#FF9000', icon: 'i-lucide-smile' },
    ],
    metricStories: {
      knowledge: {
        score: 66,
        summary:
          'Kojo remembers most taught facts but needs occasional reminders before beginning a task.',
        evidence:
          'He recalled counting patterns and common vocabulary with a short teacher prompt.',
      },
      understanding: {
        score: 63,
        summary:
          'Kojo understands familiar examples but is still developing confidence when explaining his reasoning.',
        evidence:
          'He selected correct answers more consistently than he could explain the method used.',
      },
      application: {
        score: 58,
        summary:
          'Kojo can apply learning with guidance and needs more practice starting unfamiliar tasks independently.',
        evidence:
          'He solved familiar number problems but needed the first step modelled on a new word problem.',
      },
      engagement: {
        score: 62,
        summary:
          'Kojo participates after a check in and works steadily when tasks are divided into shorter steps.',
        evidence:
          'His completion improved when the teacher set one clear goal at the beginning of the lesson.',
      },
      character: {
        score: 0,
        summary:
          'Kojo is respectful and kind while continuing to build responsibility and honesty about when he needs help.',
        evidence:
          'Character is the average of Responsibility 68%, Respect 73%, Honesty 66% and Kindness 69%.',
      },
      attitude: {
        score: 60,
        summary:
          'Kojo responds well to encouragement but can become hesitant after an incorrect answer.',
        evidence:
          'He returned to a difficult activity after feedback and completed it with guided support.',
      },
    },
    notes: [
      {
        date: '12 Jun 2026',
        text: 'Kojo recalled the counting pattern after one reminder. He understood the worked example but needed support to explain and apply the method to a new problem.',
      },
      {
        date: '5 Jun 2026',
        text: 'A short check in helped Kojo settle and participate. He accepted feedback well, returned to the task and completed it in smaller steps.',
      },
    ],
    mathematics: {
      minutes: 46,
      nextStep: {
        title: 'Explain and apply one strategy',
        description:
          'Use counters for one number bond example, then ask the student to explain the method and try a similar problem independently.',
      },
      observation:
        'Kojo shows the idea with objects but needs prompts to explain the method and apply it to a new example.',
    },
  }),
  createStudentProfile({
    id: 'abena-mensah',
    name: 'Abena Mensah',
    initials: 'AM',
    avatar: '#E7C9F6',
    streak: 3,
    timeSpent: '55m',
    lastActive: '2 days ago',
    focus:
      'Building foundational knowledge and confidence with guided practice',
    birthday: '27 Jul 2019',
    subjects: [
      {
        name: 'Mathematics',
        progress: 48,
        color: '#1678E5',
        icon: 'i-lucide-calculator',
      },
      {
        name: 'English',
        progress: 0,
        color: '#FF9000',
        icon: 'i-lucide-message-square-text',
        locked: true,
      },
      {
        name: 'Science',
        progress: 0,
        color: '#8A42F5',
        icon: 'i-lucide-flask-conical',
        locked: true,
      },
    ],
    dimensions: [
      {
        name: 'Responsibility',
        score: 56,
        color: '#FF9000',
        icon: 'i-lucide-user-round',
      },
      { name: 'Respect', score: 62, color: '#FF9000', icon: 'i-lucide-heart' },
      {
        name: 'Honesty',
        score: 58,
        color: '#FF9000',
        icon: 'i-lucide-shield-check',
      },
      { name: 'Kindness', score: 60, color: '#FF9000', icon: 'i-lucide-smile' },
    ],
    metricStories: {
      knowledge: {
        score: 49,
        summary:
          'Abena is still securing key facts and benefits from repeated, visual practice before independent work.',
        evidence:
          'She recognized some number facts and shape names but could not yet recall them consistently.',
      },
      understanding: {
        score: 45,
        summary:
          'Abena shows partial understanding when ideas are demonstrated and needs support to explain them in her own words.',
        evidence:
          'She matched concrete examples correctly but found it difficult to describe why the answer was correct.',
      },
      application: {
        score: 38,
        summary:
          'Abena needs guided practice to use new learning and follow multi step instructions.',
        evidence:
          'She completed the first step with modelling and needed continued prompts to finish the activity.',
      },
      engagement: {
        score: 52,
        summary:
          'Abena engages best in short, supported activities and can withdraw when instructions feel difficult.',
        evidence:
          'Reading instructions aloud together helped her begin and remain involved for longer.',
      },
      character: {
        score: 0,
        summary:
          'Abena is kind and respectful and is developing confidence in taking responsibility and asking honestly for help.',
        evidence:
          'Character is the average of Responsibility 56%, Respect 62%, Honesty 58% and Kindness 60%.',
      },
      attitude: {
        score: 44,
        summary:
          'Abena needs reassurance after mistakes and is learning that difficulty is a normal part of learning.',
        evidence:
          'She rejoined a counting activity after encouragement and responded positively to specific praise.',
      },
    },
    notes: [
      {
        date: '14 Jun 2026',
        text: 'Abena recognized some number facts but needed visual prompts to recall them. She understood the demonstration and required guided support to apply it independently.',
      },
      {
        date: '7 Jun 2026',
        text: 'Reading instructions aloud helped Abena engage. She became discouraged after an error but returned to the task with reassurance and specific praise.',
      },
    ],
    mathematics: {
      minutes: 32,
      nextStep: {
        title: 'Rebuild number bonds with objects',
        description:
          'Use five counters at a time. Model one number bond, ask the student to copy it, then repeat it aloud before trying another.',
      },
      observation:
        'Abena needs modelling to show number bonds and is not yet ready to explain or apply the method independently.',
    },
  }),
]

const additionalStudentSeeds: AdditionalStudentSeed[] = [
  {
    id: 'ama-agyeman',
    name: 'Ama Agyeman',
    initials: 'AA',
    avatar: '#F7D6A8',
    birthday: '4 Feb 2019',
    mathematics: 84,
    metrics: {
      knowledge: 86,
      understanding: 82,
      application: 79,
      engagement: 88,
      attitude: 85,
    },
    character: [84, 88, 86, 90],
    streak: 15,
    timeSpent: '3h 10m',
    lastActive: 'Today',
    focus: 'Extending strong number reasoning through open ended challenges',
    notes: [
      'Ama recalled number facts quickly, explained two strategies clearly and chose an efficient method for the extension problem.',
      'Ama encouraged her group, checked her work independently and used feedback to improve an already strong explanation.',
    ],
  },
  {
    id: 'yaw-ofori',
    name: 'Yaw Ofori',
    initials: 'YO',
    avatar: '#BFDDF4',
    birthday: '21 Apr 2019',
    mathematics: 73,
    metrics: {
      knowledge: 76,
      understanding: 72,
      application: 68,
      engagement: 75,
      attitude: 71,
    },
    character: [74, 78, 73, 76],
    streak: 9,
    timeSpent: '2h 20m',
    lastActive: 'Today',
    focus: 'Applying secure knowledge to unfamiliar word problems',
    notes: [
      'Yaw recalled the required facts and explained the worked example. He needed one prompt before applying the strategy to a new word problem.',
      'Yaw participated consistently and accepted correction calmly. Independent application remains the next area to strengthen.',
    ],
  },
  {
    id: 'efua-mensimah',
    name: 'Efua Mensimah',
    initials: 'EM',
    avatar: '#E4C9F5',
    birthday: '9 Jun 2019',
    mathematics: 91,
    metrics: {
      knowledge: 93,
      understanding: 90,
      application: 88,
      engagement: 92,
      attitude: 89,
    },
    character: [90, 94, 91, 93],
    streak: 21,
    timeSpent: '3h 50m',
    lastActive: 'Today',
    focus:
      'Deepening reasoning through multiple solutions and peer explanation',
    notes: [
      'Efua solved every task accurately, compared two methods and justified which strategy was most efficient.',
      'Efua listened respectfully to alternative ideas and helped a classmate by asking questions instead of giving the answer.',
    ],
  },
  {
    id: 'kofi-nyarko',
    name: 'Kofi Nyarko',
    initials: 'KN',
    avatar: '#CFE8BE',
    birthday: '17 Aug 2019',
    mathematics: 77,
    metrics: {
      knowledge: 80,
      understanding: 76,
      application: 73,
      engagement: 78,
      attitude: 75,
    },
    character: [79, 76, 78, 81],
    streak: 11,
    timeSpent: '2h 35m',
    lastActive: 'Yesterday',
    focus: 'Building confidence when explaining independent solutions',
    notes: [
      'Kofi completed the number activities independently and used the correct strategy, although his verbal explanation was brief.',
      'Kofi remained focused, checked a corrected answer and showed responsibility by completing unfinished practice.',
    ],
  },
  {
    id: 'akosua-frimpong',
    name: 'Akosua Frimpong',
    initials: 'AF',
    avatar: '#FFD5C2',
    birthday: '30 Oct 2019',
    mathematics: 72,
    metrics: {
      knowledge: 74,
      understanding: 71,
      application: 67,
      engagement: 79,
      attitude: 76,
    },
    character: [77, 83, 75, 82],
    streak: 10,
    timeSpent: '2h 25m',
    lastActive: 'Today',
    focus: 'Transferring good classroom understanding into independent work',
    notes: [
      'Akosua explained the class example accurately and stayed highly engaged. She needed support when the numbers changed in the independent task.',
      'Akosua responded positively to feedback and showed kindness by sharing materials and taking turns during partner work.',
    ],
  },
  {
    id: 'nana-addo',
    name: 'Nana Addo',
    initials: 'NA',
    avatar: '#C8DCF3',
    birthday: '13 Jan 2019',
    mathematics: 69,
    metrics: {
      knowledge: 71,
      understanding: 67,
      application: 62,
      engagement: 70,
      attitude: 68,
    },
    character: [70, 74, 68, 72],
    streak: 8,
    timeSpent: '2h 5m',
    lastActive: 'Today',
    focus: 'Explaining methods and applying them without the first step prompt',
    notes: [
      'Nana recalled most number facts and followed the model. He needed a prompt to explain why the strategy worked and begin the transfer task.',
      'Nana worked steadily after a brief check in and corrected his answer without frustration when given specific feedback.',
    ],
  },
  {
    id: 'esi-quartey',
    name: 'Esi Quartey',
    initials: 'EQ',
    avatar: '#F3C8DA',
    birthday: '25 Mar 2019',
    mathematics: 62,
    metrics: {
      knowledge: 65,
      understanding: 61,
      application: 56,
      engagement: 66,
      attitude: 63,
    },
    character: [67, 72, 65, 70],
    streak: 6,
    timeSpent: '1h 50m',
    lastActive: 'Yesterday',
    focus: 'Using concrete examples before moving to independent problems',
    notes: [
      'Esi recognized the number pattern and explained part of the method using counters. Independent application still required guided questions.',
      'Esi collaborated well and remained engaged when the task was divided into short, clearly explained steps.',
    ],
  },
  {
    id: 'kwaku-mensah',
    name: 'Kwaku Mensah',
    initials: 'KM',
    avatar: '#D4E7B9',
    birthday: '6 May 2019',
    mathematics: 57,
    metrics: {
      knowledge: 60,
      understanding: 57,
      application: 51,
      engagement: 59,
      attitude: 55,
    },
    character: [62, 69, 61, 66],
    streak: 5,
    timeSpent: '1h 30m',
    lastActive: 'Yesterday',
    focus: 'Strengthening confidence and applying one familiar strategy',
    notes: [
      'Kwaku recalled the core facts with a visual reminder and could copy the modelled strategy. He was not yet ready to apply it alone.',
      'Kwaku became hesitant after an error but returned to the activity after reassurance and completed the final two questions.',
    ],
  },
  {
    id: 'adjoa-sarpong',
    name: 'Adjoa Sarpong',
    initials: 'AS',
    avatar: '#F7D9A9',
    birthday: '18 Jul 2019',
    mathematics: 68,
    metrics: {
      knowledge: 70,
      understanding: 66,
      application: 63,
      engagement: 73,
      attitude: 70,
    },
    character: [72, 80, 71, 78],
    streak: 8,
    timeSpent: '2h 10m',
    lastActive: 'Today',
    focus: 'Turning careful explanations into faster independent application',
    notes: [
      'Adjoa recalled the facts and explained her method carefully. She worked slowly and needed extra time to finish the independent example.',
      'Adjoa showed strong respect and kindness in partner work and responded thoughtfully when her method was questioned.',
    ],
  },
  {
    id: 'sena-tetteh',
    name: 'Sena Tetteh',
    initials: 'ST',
    avatar: '#C9E2E8',
    birthday: '2 Sep 2019',
    mathematics: 60,
    metrics: {
      knowledge: 63,
      understanding: 59,
      application: 54,
      engagement: 64,
      attitude: 61,
    },
    character: [65, 70, 64, 68],
    streak: 6,
    timeSpent: '1h 40m',
    lastActive: 'Today',
    focus: 'Practising multi step instructions with fewer reminders',
    notes: [
      'Sena understood the demonstrated example and recalled the first step. He needed reminders to follow the full sequence independently.',
      'Sena participated well in the practical activity and accepted feedback, especially when instructions were given one step at a time.',
    ],
  },
  {
    id: 'mabel-darko',
    name: 'Mabel Darko',
    initials: 'MD',
    avatar: '#E8CEF2',
    birthday: '15 Feb 2019',
    mathematics: 54,
    metrics: {
      knowledge: 56,
      understanding: 52,
      application: 46,
      engagement: 58,
      attitude: 53,
    },
    character: [60, 68, 59, 66],
    streak: 4,
    timeSpent: '1h 20m',
    lastActive: '2 days ago',
    focus: 'Securing number facts before attempting independent application',
    notes: [
      'Mabel recalled some number facts with counters but confused two combinations. She needed modelling throughout the application task.',
      'Mabel stayed involved during the practical activity and asked for help appropriately, but she needs reassurance after mistakes.',
    ],
  },
  {
    id: 'daniel-koomson',
    name: 'Daniel Koomson',
    initials: 'DK',
    avatar: '#BFD8EC',
    birthday: '28 Apr 2019',
    mathematics: 49,
    metrics: {
      knowledge: 52,
      understanding: 47,
      application: 41,
      engagement: 50,
      attitude: 46,
    },
    character: [55, 64, 57, 61],
    streak: 3,
    timeSpent: '1h 5m',
    lastActive: '2 days ago',
    focus: 'Rebuilding confidence through short visual number activities',
    notes: [
      'Daniel recognized the quantities when objects were present but could not consistently recall the matching number fact without them.',
      'Daniel withdrew after an incorrect answer. A shorter task and immediate praise helped him return and complete the final example.',
    ],
  },
  {
    id: 'afia-badu',
    name: 'Afia Badu',
    initials: 'AB',
    avatar: '#F6CDB8',
    birthday: '11 Jun 2019',
    mathematics: 43,
    metrics: {
      knowledge: 46,
      understanding: 42,
      application: 35,
      engagement: 48,
      attitude: 43,
    },
    character: [57, 63, 55, 65],
    streak: 2,
    timeSpent: '50m',
    lastActive: '2 days ago',
    focus: 'Recognizing quantities and following one step instructions',
    notes: [
      'Afia matched small quantities with support and needed repeated modelling to explain or use the number relationship.',
      'Afia engaged for longer when she could move the counters herself. She responded well to praise for each completed step.',
    ],
  },
  {
    id: 'gideon-amankwah',
    name: 'Gideon Amankwah',
    initials: 'GA',
    avatar: '#CADCF2',
    birthday: '23 Aug 2019',
    mathematics: 37,
    metrics: {
      knowledge: 40,
      understanding: 36,
      application: 30,
      engagement: 42,
      attitude: 38,
    },
    character: [52, 60, 54, 58],
    streak: 1,
    timeSpent: '35m',
    lastActive: '2 days ago',
    focus: 'Developing foundational counting skills through daily support',
    notes: [
      'Gideon counted accurately to five with one to one support and lost track when the set became larger. He needs repeated concrete practice.',
      'Gideon was reluctant to begin but participated in a short matching game after the teacher modelled the first turn.',
    ],
  },
  {
    id: 'naa-dedei',
    name: 'Naa Dedei',
    initials: 'ND',
    avatar: '#E2C8F0',
    birthday: '7 Nov 2019',
    mathematics: 52,
    metrics: {
      knowledge: 54,
      understanding: 50,
      application: 44,
      engagement: 56,
      attitude: 51,
    },
    character: [61, 70, 60, 68],
    streak: 4,
    timeSpent: '1h 15m',
    lastActive: 'Yesterday',
    focus: 'Explaining concrete examples before moving to symbols',
    notes: [
      'Naa represented the problem with counters but could not yet explain the same idea using number symbols without support.',
      'Naa worked cooperatively and persisted with encouragement. She benefits from verbal rehearsal before writing an answer.',
    ],
  },
]

const students: TeacherStudent[] = [
  ...coreStudents,
  ...additionalStudentSeeds.map(createAdditionalStudent),
]

export type TeacherClass = {
  id: string
  name: string
  grade: string
  term: string
  studentCount: number
}

const _legacyTeacherClasses: TeacherClass[] = [
  {
    id: 'class-1ba',
    name: 'Class 1BA',
    grade: 'Primary 1',
    term: 'Term 2',
    studentCount: students.length,
  },
  {
    id: 'class-1bb',
    name: 'Class 1BB',
    grade: 'Primary 1',
    term: 'Term 2',
    studentCount: 16,
  },
  {
    id: 'class-2a',
    name: 'Class 2A',
    grade: 'Primary 2',
    term: 'Term 2',
    studentCount: 14,
  },
]

const class1BBNames = [
  'Akosua Frimpong',
  'Nana Yeboah',
  'Maame Serwaa',
  'Kofi Antwi',
  'Esi Nyarko',
  'Yaw Asare',
  'Adwoa Danso',
  'Kwesi Addo',
  'Abigail Ofori',
  'Joel Ankrah',
  'Esther Quaye',
  'Michael Tandoh',
  'Sarah Agyeman',
  'Elijah Poku',
  'Ruth Appiah',
  'Isaac Lartey',
]

const class2ANames = [
  'Naomi Kusi',
  'Samuel Owusu',
  'Grace Arthur',
  'Emmanuel Adjei',
  'Priscilla Amoah',
  'David Annan',
  'Joana Essel',
  'Benjamin Osei',
  'Miriam Baah',
  'Josephine Tackie',
  'Nathaniel Doku',
  'Gloria Acquah',
  'Caleb Lamptey',
  'Victoria Nartey',
]

function clampScore(score: number) {
  return Math.max(20, Math.min(98, score))
}

function createClassRoster(
  classId: string,
  names: string[],
  scoreAdjustment: number,
): TeacherStudent[] {
  return names.map((name, index) => {
    const source = students[index % students.length]!
    const adjustment = scoreAdjustment + ((index % 5) - 2) * 2
    const progress = clampScore(source.progress + adjustment)
    const initials = name
      .split(' ')
      .map((part) => part[0])
      .join('')
      .slice(0, 2)
      .toUpperCase()

    return {
      ...source,
      id: `${classId}-${name.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`,
      name,
      initials,
      progress,
      status: getStatus(progress),
      streak: Math.max(1, source.streak + (index % 3) - 1),
      lastActive:
        index % 7 === 0
          ? '2 days ago'
          : index % 3 === 0
            ? 'Yesterday'
            : 'Today',
      progressMetrics: source.progressMetrics.map((metric) => ({
        ...metric,
        score: clampScore(metric.score + adjustment),
      })),
      subjects: source.subjects.map((subject) => ({
        ...subject,
        progress: subject.locked
          ? subject.progress
          : clampScore(subject.progress + adjustment),
      })),
      dimensions: source.dimensions.map((dimension) => ({
        ...dimension,
        score: clampScore(dimension.score + adjustment),
        color: getProgressStatusColor(clampScore(dimension.score + adjustment)),
      })),
    }
  })
}

const _classRosters: Record<string, TeacherStudent[]> = {
  'class-1ba': students,
  'class-1bb': createClassRoster('class-1bb', class1BBNames, 1),
  'class-2a': createClassRoster('class-2a', class2ANames, 6),
}

export function useTeacherDemo() {
  // Kept under the historical export name while callers are migrated. Unlike
  // the former fixture, every returned record is loaded from authorised APIs.
  const currentRoleStore = useCurrentRoleStore()
  const { currentRole } = storeToRefs(currentRoleStore)
  const teacherName = computed(() => currentRole.value?.schoolName ?? 'Teacher')
  const teacherClasses = reactive<TeacherClass[]>([])
  const students = ref<TeacherStudent[]>([])
  const selectedClassId = useState<string>('teacher-selected-class', () => '')
  const selectedClass = computed(
    () =>
      teacherClasses.find(
        (teacherClass) => teacherClass.id === selectedClassId.value,
      ) ?? {
        id: '',
        name: 'No class selected',
        grade: '',
        term: '',
        studentCount: 0,
      },
  )
  const selectedStudents = computed(() => students.value)
  const className = computed(() => selectedClass.value.name)
  const classAverage = computed(() =>
    selectedStudents.value.length
      ? average(selectedStudents.value.map((student) => student.progress))
      : 0,
  )

  const emptyMathematicsDetail = (): MathematicsDetail => ({
    metrics: [],
    weeks: [],
    nextTeachingStep: {
      title: 'No recommendation yet',
      description: 'Record learning activity to generate a recommendation.',
    },
    evidence: {
      weekLabel: 'No classroom evidence yet',
      checks: { shows: false, explains: false, applies: false },
      items: [
        { key: 'shows', label: 'Shows understanding' },
        { key: 'explains', label: 'Explains their thinking' },
        { key: 'applies', label: 'Applies learning independently' },
      ],
      observation: '',
    },
  })
  const cache = useLocalDataCache()
  const loading = ref(true)
  let loadVersion = 0
  const load = async () => {
    // Supabase's browser session is not available to this shared dashboard
    // composable during SSR. Loading then causes a server-side 401 and an
    // unhandled rejection before the client has hydrated its session.
    if (!import.meta.client) return
    const version = ++loadVersion
    const schoolId = currentRole.value?.schoolId
    // The shared app header also mounts this composable for learners and
    // guardians. They cannot read the staff roster endpoint.
    if (
      !schoolId ||
      !['teacher', 'admin', 'system_admin'].includes(currentRole.value?.role)
    ) {
      teacherClasses.splice(0, teacherClasses.length)
      students.value = []
      loading.value = false
      return
    }
    const scope = cache.scope()
    const cacheKey = `teacher-dashboard:${selectedClassId.value}`
    type Snapshot = {
      classes: TeacherClass[]
      students: TeacherStudent[]
      classId: string
    }
    const cached = cache.read<Snapshot>(cacheKey)
    if (cached) {
      teacherClasses.splice(0, teacherClasses.length, ...cached.classes)
      students.value = cached.students
      selectedClassId.value = cached.classId
    } else {
      students.value = []
    }
    loading.value = !cached
    try {
      let classesResponse: {
        data: Array<{ id: string; className: string; classYear: string }>
      }
      let usersResponse: { data: SchoolUser[] }
      try {
        ;[classesResponse, usersResponse] = await Promise.all([
          $fetch<{
            data: Array<{ id: string; className: string; classYear: string }>
          }>('/api/classes', { query: { schoolId } }),
          $fetch<{ data: SchoolUser[] }>('/api/users', {
            query: { schoolId, role: 'student' },
          }),
        ])
      } catch (error) {
        console.error('Could not load teacher dashboard data', error)
        return
      }
      if (version !== loadVersion || scope !== cache.scope()) return
      const classRows = classesResponse.data ?? []
      teacherClasses.splice(
        0,
        teacherClasses.length,
        ...classRows.map((item) => ({
          id: item.id,
          name: item.className,
          grade: item.classYear,
          term: 'Current term',
          studentCount: (usersResponse.data ?? []).filter((student) =>
            student.classes?.some(
              (studentClass) => studentClass.id === item.id,
            ),
          ).length,
        })),
      )
      if (!teacherClasses.some((item) => item.id === selectedClassId.value))
        selectedClassId.value = teacherClasses[0]?.id ?? ''
      const roster = (usersResponse.data ?? []).filter((student) =>
        student.classes?.some(
          (studentClass) => studentClass.id === selectedClassId.value,
        ),
      )
      const summaries = await Promise.all(
        roster.map(async (student) => {
          try {
            return await $fetch<{
              data: {
                accuracy: number
                completion: number
                timeSpentSeconds: number
                lastActivityAt: string | null
                metrics: Array<{
                  key: ProgressMetricKey
                  label: string
                  score: number
                  icon: string
                  description: string
                  measured: boolean
                }>
              }
            }>('/api/learning/summary', {
              query: { schoolId, userId: student.userId },
            })
          } catch (error) {
            if (cached) throw error
            return {
              data: {
                accuracy: 0,
                completion: 0,
                timeSpentSeconds: 0,
                lastActivityAt: null,
                metrics: [],
              },
            }
          }
        }),
      )
      if (version !== loadVersion || scope !== cache.scope()) return
      students.value = roster.map((student, index) => {
        const summary = summaries[index]!.data
        const name =
          `${student.firstName} ${student.lastName}`.trim() || student.email
        const progress = Math.round((summary.accuracy + summary.completion) / 2)
        const metrics = summary.metrics.map((metric) => ({
          ...metric,
          meaning: '',
          summary: metric.measured
            ? 'Calculated from recorded learning.'
            : 'No recorded evidence yet.',
          evidence: metric.measured
            ? 'Recorded learning activity'
            : 'Not yet measured',
        }))
        return {
          id: student.userId,
          name,
          initials:
            name
              .split(/\s+/)
              .map((part) => part[0] ?? '')
              .join('')
              .slice(0, 2)
              .toUpperCase() || 'S',
          avatar: ['#dceeff', '#fff0c9', '#e4f8e8', '#f4e5ff'][index % 4]!,
          status: getStatus(progress),
          progress,
          progressMetrics: metrics,
          streak: 0,
          timeSpent: summary.timeSpentSeconds
            ? `${Math.round(summary.timeSpentSeconds / 60)} min`
            : '0 min',
          lastActive: summary.lastActivityAt
            ? new Date(summary.lastActivityAt).toLocaleDateString()
            : 'Not active yet',
          focus:
            metrics.sort((a, b) => a.score - b.score)[0]?.label ??
            'No recorded activity',
          birthday: 'Not recorded',
          subjects: [],
          dimensions: metrics.map((metric) => ({
            name: metric.label,
            score: metric.score,
            color: '#1678e5',
            icon: metric.icon,
          })),
          notes: [],
          mathematicsDetail: emptyMathematicsDetail(),
        }
      })
      const snapshot = {
        classes: [...teacherClasses],
        students: students.value,
        classId: selectedClassId.value,
      }
      cache.write(cacheKey, snapshot, scope)
      cache.write(`teacher-dashboard:${selectedClassId.value}`, snapshot, scope)
    } catch (error) {
      console.error('Could not refresh teacher progress', error)
    } finally {
      if (version === loadVersion) loading.value = false
    }
  }
  watch(
    [
      () => currentRole.value?.schoolId,
      () => currentRole.value?.role,
      selectedClassId,
    ],
    () => {
      void load()
    },
    { immediate: true },
  )

  function selectClass(classId: string) {
    if (teacherClasses.some((teacherClass) => teacherClass.id === classId)) {
      selectedClassId.value = classId
    }
  }

  function getClassStudents(classId: string): TeacherStudent[] {
    return classId === selectedClassId.value ? selectedStudents.value : []
  }

  function getStudent(id: string): TeacherStudent {
    return (
      selectedStudents.value.find((student) => student.id === id) ??
      selectedStudents.value[0]!
    )
  }

  function getMathematicsDetail(studentId: string): MathematicsDetail {
    return getStudent(studentId)?.mathematicsDetail ?? emptyMathematicsDetail()
  }

  return {
    loading,
    students: selectedStudents,
    teacherClasses,
    selectedClassId,
    selectedClass,
    selectClass,
    getClassStudents,
    className,
    teacherName,
    classAverage,
    getStudent,
    getMathematicsDetail,
  }
}
